import {useCallback, useMemo, useState} from 'react';
import {SearchFilterTargetVO} from '../../../api';
import {CheckState, CiselnikFilterNode} from './filterTreeTypes';

export interface UseFilterTreeResult {
    /** Aktuálně zaškrtnuté listové hodnoty (jediný zdroj pravdy). */
    checkedIds: Set<number>;
    /** Rozbalené kategorie. */
    expandedIds: Set<number>;
    /** Tri-state uzlu (kaskáda přes potomky). */
    getState: (node: CiselnikFilterNode) => CheckState;
    /** Přepne zaškrtnutí uzlu – u kategorie kaskádou na všechny jeho listy. */
    toggleNode: (node: CiselnikFilterNode) => void;
    /** Rozbalí/sbalí kategorii. */
    toggleExpanded: (id: number) => void;
    /** Zruší veškerý výběr = „Vynulovat filtr" (prázdný výběr znamená „nefiltruj, zobraz vše"). */
    clearAll: () => void;
    /** Nahradí celý výběr danou množinou listů (obnova snímku při zrušení změn filtru). */
    replaceChecked: (ids: Iterable<number>) => void;
    /** Odškrtne celý podstrom uzlu (všechny jeho listy) – použito křížkem na tagu zafiltrované hodnoty. */
    clearNode: (node: CiselnikFilterNode) => void;
    /** Počet zaškrtnutých listových hodnot. */
    selectedCount: number;
    /** Filtrovací cíle ({@link SearchFilterTargetVO}) zaškrtnutých listů – vstup pro filtr mapového enginu. */
    getCheckedTargets: () => SearchFilterTargetVO[];
}

/**
 * Drží stav stromového filtru: zaškrtnuté listy a rozbalené kategorie.
 *
 * Model kaskádové tri-state: pravdou je pouze množina zaškrtnutých LISTŮ.
 * Stav rodiče se dopočítává ({@link resolveState}) – všechny listy zaškrtnuté =
 * „checked", žádný = „unchecked", jinak „indeterminate". Přepnutí kategorie
 * nastaví všechny její listy naráz, takže stav nemůže být nekonzistentní.
 *
 * VÝCHOZÍ STAV JE PRÁZDNÝ – nic není zaškrtnuté a filtr nijak neomezuje (zobrazuje se vše).
 */
export const useFilterTree = (tree: CiselnikFilterNode[]): UseFilterTreeResult => {
    const [checkedIds, setCheckedIds] = useState<Set<number>>(() => new Set());
    const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());

    // Předpočítané listy každého uzlu (jednou při změně stromu) → O(1) místo procházení podstromu při každém renderu.
    const leafIdsByNodeId = useMemo(() => {
        const map = new Map<number, number[]>();
        const collect = (node: CiselnikFilterNode): number[] => {
            const leaves = !node.children || node.children.length === 0 ? [node.id] : node.children.flatMap(collect);
            map.set(node.id, leaves);
            return leaves;
        };
        tree.forEach(collect);
        return map;
    }, [tree]);

    // Předpočítaný tri-state VŠECH uzlů (jedním průchodem při změně stromu/výběru) → render jen čte z mapy (O(1)).
    const stateByNodeId = useMemo(() => {
        const map = new Map<number, CheckState>();
        const compute = (node: CiselnikFilterNode): CheckState => {
            let state: CheckState;
            if (!node.children || node.children.length === 0) {
                state = checkedIds.has(node.id) ? 'checked' : 'unchecked';
            } else {
                const childStates = node.children.map(compute);
                state = childStates.every(s => s === 'checked')
                    ? 'checked'
                    : childStates.every(s => s === 'unchecked')
                      ? 'unchecked'
                      : 'indeterminate';
            }
            map.set(node.id, state);
            return state;
        };
        tree.forEach(compute);
        return map;
    }, [tree, checkedIds]);

    const getState = useCallback((node: CiselnikFilterNode) => stateByNodeId.get(node.id) ?? 'unchecked', [stateByNodeId]);

    // Mapa list → jeho filtrovací cíl (jen listy s vyplněným targetem). Přepočítá se jen při změně stromu.
    const targetByLeafId = useMemo(() => {
        const map = new Map<number, SearchFilterTargetVO>();
        const walk = (nodes: CiselnikFilterNode[]): void => {
            nodes.forEach(n => {
                if (n.children && n.children.length > 0) {
                    walk(n.children);
                } else if (n.target) {
                    map.set(n.id, n.target);
                }
            });
        };
        walk(tree);
        return map;
    }, [tree]);

    const getCheckedTargets = useCallback(
        (): SearchFilterTargetVO[] =>
            Array.from(checkedIds)
                .map(id => targetByLeafId.get(id))
                .filter((t): t is SearchFilterTargetVO => Boolean(t)),
        [checkedIds, targetByLeafId],
    );

    const toggleNode = useCallback(
        (node: CiselnikFilterNode) => {
            const leaves = leafIdsByNodeId.get(node.id) ?? [node.id];
            setCheckedIds(prev => {
                const next = new Set(prev);
                // Sbalený stav → zaškrtnout vše; jinak (částečně i plně) → odškrtnout vše.
                const allChecked = leaves.every(id => next.has(id));
                if (allChecked) {
                    leaves.forEach(id => next.delete(id));
                } else {
                    leaves.forEach(id => next.add(id));
                }
                return next;
            });
        },
        [leafIdsByNodeId],
    );

    const toggleExpanded = useCallback((id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const clearAll = useCallback(() => setCheckedIds(new Set()), []);

    const replaceChecked = useCallback((ids: Iterable<number>) => setCheckedIds(new Set(ids)), []);

    const clearNode = useCallback(
        (node: CiselnikFilterNode) => {
            const leaves = leafIdsByNodeId.get(node.id) ?? [node.id];
            setCheckedIds(prev => {
                const next = new Set(prev);
                leaves.forEach(id => next.delete(id));
                return next;
            });
        },
        [leafIdsByNodeId],
    );

    const selectedCount = checkedIds.size;

    return useMemo(
        () => ({checkedIds, expandedIds, getState, toggleNode, toggleExpanded, clearAll, replaceChecked, clearNode, selectedCount, getCheckedTargets}),
        [checkedIds, expandedIds, getState, toggleNode, toggleExpanded, clearAll, replaceChecked, clearNode, selectedCount, getCheckedTargets],
    );
};
